import { fetchPhotoBlob } from './api';

// Object URLs of photos fetched with the JWT, shared by every AuthImage / PhotoGallery for the session.
// Each user holds a reference; an unused URL is kept for reuse until there are too many, then revoked.
const KEEP_UNUSED = { thumb: 80, full: 4 };
const entries = new Map(); // "variant:id" -> { id, variant, url, blob, promise, refs, used }

const keyOf = (id, variant) => `${variant}:${id}`;

// another login in the same tab (new token) must not see the previous user's photos
let owner;
function checkOwner() {
    let token = null;
    try {
        token = localStorage.getItem('token');
    } catch (e) {
        token = null;
    }
    if (owner !== undefined && token !== owner) clearPhotoCache();
    owner = token;
}

function prune(variant) {
    const idle = [...entries.values()]
        .filter(e => e.variant === variant && e.refs <= 0 && e.url)
        .sort((a, b) => a.used - b.used);
    const extra = idle.length - KEEP_UNUSED[variant];
    for (let i = 0; i < extra; i++) {
        URL.revokeObjectURL(idle[i].url);
        entries.delete(keyOf(idle[i].id, idle[i].variant));
    }
}

/** Takes a reference on the photo's object URL (fetching it the first time); give it back with releasePhoto */
export function acquirePhoto(id, variant = 'thumb') {
    checkOwner();
    const key = keyOf(id, variant);
    let entry = entries.get(key);
    if (!entry) {
        entry = { id, variant, url: null, blob: null, refs: 0, used: Date.now() };
        entry.promise = fetchPhotoBlob(id, variant).then(
            blob => {
                entry.blob = blob;
                entry.url = URL.createObjectURL(blob);
                if (entry.refs <= 0) prune(variant);
                return entry.url;
            },
            error => {
                if (entries.get(key) === entry) entries.delete(key); // the next try fetches again
                throw error;
            },
        );
        entries.set(key, entry);
    }
    entry.dropped = false;
    entry.refs += 1;
    entry.used = Date.now();
    return entry.promise;
}

export function releasePhoto(id, variant = 'thumb') {
    const entry = entries.get(keyOf(id, variant));
    if (!entry) return;
    entry.refs = Math.max(0, entry.refs - 1);
    entry.used = Date.now();
    if (entry.refs > 0) return;
    if (entry.dropped) {
        if (entry.url) URL.revokeObjectURL(entry.url);
        entries.delete(keyOf(id, variant));
    } else {
        prune(variant);
    }
}

/** The object URL if it is already loaded (no reference taken) */
export function cachedPhotoUrl(id, variant = 'thumb') {
    const entry = entries.get(keyOf(id, variant));
    return entry ? entry.url : null;
}

/** Seeds the cache with a blob we already have (a photo just uploaded): no download to show it */
export function primePhoto(id, variant, blob) {
    checkOwner();
    const key = keyOf(id, variant);
    const old = entries.get(key);
    if (old && old.url) return old.url;
    const url = URL.createObjectURL(blob);
    const entry = { id, variant, url, blob, refs: old ? old.refs : 0, used: Date.now(), promise: Promise.resolve(url) };
    entries.set(key, entry);
    prune(variant);
    return url;
}

/** Forgets a photo (removed before saving); a URL still on screen is revoked when its component lets go */
export function dropPhoto(id) {
    ['thumb', 'full'].forEach(variant => {
        const key = keyOf(id, variant);
        const entry = entries.get(key);
        if (!entry) return;
        if (entry.refs > 0) {
            entry.dropped = true; // revoked when the last user lets go
            return;
        }
        entries.delete(key);
        if (entry.url) URL.revokeObjectURL(entry.url);
    });
}

/** Revokes everything (e.g. on logout: label photos must not stay in memory) */
export function clearPhotoCache() {
    entries.forEach(entry => {
        if (entry.url) URL.revokeObjectURL(entry.url);
    });
    entries.clear();
}

/** Saves the full photo to the device */
export async function downloadPhoto(id, name) {
    const url = await acquirePhoto(id, 'full');
    try {
        const entry = entries.get(keyOf(id, 'full'));
        const type = (entry && entry.blob && entry.blob.type) || 'image/jpeg';
        const ext = type === 'image/png' ? 'png' : type === 'image/webp' ? 'webp' : 'jpg';
        const link = document.createElement('a');
        link.href = url;
        link.download = `${name || 'fotografie-' + id}.${ext}`;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    } finally {
        // let the browser start reading the URL before it can be revoked
        setTimeout(() => releasePhoto(id, 'full'), 4000);
    }
}
